import { createSeedConnections, getDiscoveryForConnection } from './syntheticData';
import type {
  ConnectionFormValues,
  McpDiscoveredResource,
  McpDiscoveredTool,
  McpServerConnection,
} from './types';

const STORAGE_KEY = 'mcp-resources-v2';

function parseJsonObject(text: string): Record<string, string> {
  const trimmed = text.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        out[k] = v == null ? '' : String(v);
      }
      return out;
    }
  } catch {
    /* fall through to line parser */
  }
  const out: Record<string, string> = {};
  for (const line of trimmed.split('\n')) {
    const idx = line.indexOf('=');
    if (idx <= 0) continue;
    out[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  }
  return out;
}

function parseArgs(text: string): string[] {
  return text
    .split(/\n|,/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function parseTags(text: string): string[] {
  return text
    .split(/,|\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function formValuesToPartial(values: ConnectionFormValues): Omit<
  McpServerConnection,
  'id' | 'status' | 'tools' | 'resources' | 'createdAt' | 'updatedAt' | 'lastTestedAt' | 'lastError'
> {
  const isStdio = values.transport === 'stdio';
  return {
    name: values.name.trim(),
    description: values.description.trim() || undefined,
    transport: values.transport,
    command: isStdio ? values.command.trim() || undefined : undefined,
    args: isStdio ? parseArgs(values.argsText) : undefined,
    env: isStdio ? parseJsonObject(values.envText) : undefined,
    url: !isStdio ? values.url.trim() || undefined : undefined,
    headers: !isStdio ? parseJsonObject(values.headersText) : undefined,
    authType: values.authType,
    authValue: values.authType === 'none' ? undefined : values.authValue.trim() || undefined,
    tags: parseTags(values.tagsText),
    isActive: values.isActive,
    source: values.source,
    catalogId: values.catalogId,
  };
}

export function connectionToFormValues(conn: McpServerConnection): ConnectionFormValues {
  return {
    name: conn.name,
    description: conn.description ?? '',
    transport: conn.transport,
    command: conn.command ?? '',
    argsText: (conn.args ?? []).join('\n'),
    envText: conn.env && Object.keys(conn.env).length ? JSON.stringify(conn.env, null, 2) : '',
    url: conn.url ?? '',
    headersText:
      conn.headers && Object.keys(conn.headers).length ? JSON.stringify(conn.headers, null, 2) : '',
    authType: conn.authType,
    authValue: conn.authValue ?? '',
    tagsText: conn.tags.join(', '),
    isActive: conn.isActive,
    source: conn.source,
    catalogId: conn.catalogId,
  };
}

export function emptyFormValues(): ConnectionFormValues {
  return {
    name: '',
    description: '',
    transport: 'stdio',
    command: 'npx',
    argsText: '',
    envText: '',
    url: '',
    headersText: '',
    authType: 'none',
    authValue: '',
    tagsText: '',
    isActive: true,
    source: 'custom',
  };
}

function validateConnectionConfig(
  conn: Pick<McpServerConnection, 'transport' | 'command' | 'url' | 'authType' | 'authValue'>
): string | null {
  if (conn.transport === 'stdio') {
    if (!conn.command?.trim()) return 'Command is required for stdio transport';
  } else if (!conn.url?.trim()) {
    return 'URL is required for remote transports';
  }
  if (conn.authType !== 'none' && !conn.authValue?.trim()) {
    return `Auth value is required for ${conn.authType}`;
  }
  return null;
}

function loadFromStorage(): McpServerConnection[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return parsed as McpServerConnection[];
  } catch {
    return null;
  }
}

function saveToStorage(connections: McpServerConnection[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(connections));
  } catch {
    /* ignore quota errors in synthetic demo */
  }
}

export function loadConnections(): McpServerConnection[] {
  const stored = loadFromStorage();
  if (stored && stored.length > 0) return stored;
  const seed = createSeedConnections();
  saveToStorage(seed);
  return seed;
}

export function persistConnections(connections: McpServerConnection[]) {
  saveToStorage(connections);
}

export function createConnectionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `conn-${crypto.randomUUID()}`;
  }
  return `conn-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function addConnection(
  connections: McpServerConnection[],
  values: ConnectionFormValues
): McpServerConnection[] {
  const ts = new Date().toISOString();
  const partial = formValuesToPartial(values);
  const next: McpServerConnection = {
    ...partial,
    id: createConnectionId(),
    status: 'never_tested',
    tools: [],
    resources: [],
    createdAt: ts,
    updatedAt: ts,
  };
  const list = [next, ...connections];
  persistConnections(list);
  return list;
}

export function updateConnection(
  connections: McpServerConnection[],
  id: string,
  values: ConnectionFormValues
): McpServerConnection[] {
  const ts = new Date().toISOString();
  const partial = formValuesToPartial(values);
  const list = connections.map((c) =>
    c.id === id
      ? {
          ...c,
          ...partial,
          updatedAt: ts,
          // keep discovery state unless transport identity changes drastically — keep as-is
        }
      : c
  );
  persistConnections(list);
  return list;
}

export function deleteConnection(
  connections: McpServerConnection[],
  id: string
): McpServerConnection[] {
  const list = connections.filter((c) => c.id !== id);
  persistConnections(list);
  return list;
}

export function toggleConnectionActive(
  connections: McpServerConnection[],
  id: string
): McpServerConnection[] {
  const ts = new Date().toISOString();
  const list = connections.map((c) =>
    c.id === id ? { ...c, isActive: !c.isActive, updatedAt: ts } : c
  );
  persistConnections(list);
  return list;
}

export function setConnectionTesting(
  connections: McpServerConnection[],
  id: string
): McpServerConnection[] {
  const list = connections.map((c) =>
    c.id === id ? { ...c, status: 'testing' as const, lastError: undefined } : c
  );
  persistConnections(list);
  return list;
}

export function applyTestResult(
  connections: McpServerConnection[],
  id: string
): McpServerConnection[] {
  const ts = new Date().toISOString();
  const list = connections.map((c) => {
    if (c.id !== id) return c;
    const err = validateConnectionConfig(c);
    if (err) {
      return {
        ...c,
        status: 'failed' as const,
        lastTestedAt: ts,
        lastError: err,
        updatedAt: ts,
      };
    }
    const discovered = getDiscoveryForConnection(c);
    // Preserve previously enabled flags by name/uri when re-testing
    const prevToolEnabled = new Map(c.tools.map((t) => [t.name, t.enabled]));
    const prevResEnabled = new Map(c.resources.map((r) => [r.uri, r.enabled]));
    const tools: McpDiscoveredTool[] = discovered.tools.map((t) => ({
      ...t,
      enabled: prevToolEnabled.get(t.name) ?? t.enabled,
    }));
    const resources: McpDiscoveredResource[] = discovered.resources.map((r) => ({
      ...r,
      enabled: prevResEnabled.get(r.uri) ?? r.enabled,
    }));
    return {
      ...c,
      status: 'connected' as const,
      lastTestedAt: ts,
      lastError: undefined,
      tools,
      resources,
      updatedAt: ts,
    };
  });
  persistConnections(list);
  return list;
}

export function saveDiscoverySelections(
  connections: McpServerConnection[],
  id: string,
  toolEnabled: Record<string, boolean>,
  resourceEnabled: Record<string, boolean>
): McpServerConnection[] {
  const ts = new Date().toISOString();
  const list = connections.map((c) => {
    if (c.id !== id) return c;
    return {
      ...c,
      tools: c.tools.map((t) => ({
        ...t,
        enabled: toolEnabled[t.id] ?? t.enabled,
      })),
      resources: c.resources.map((r) => ({
        ...r,
        enabled: resourceEnabled[r.id] ?? r.enabled,
      })),
      updatedAt: ts,
    };
  });
  persistConnections(list);
  return list;
}

export function resetStoreToSeed(): McpServerConnection[] {
  const seed = createSeedConnections();
  persistConnections(seed);
  return seed;
}
